// [PURPOSE]: Unregister legacy service workers and clear their caches on next page load.
// [WHY]: The original public/sw.js (cache-first, static CACHE_NAME, cached non-existent
//        /static/js/bundle.js paths) is the root cause of post-deploy ChunkLoadError.
//        Removing the registration call in src/index.js stops new installs but does not
//        affect returning users whose browsers already have the SW installed — those
//        SWs keep running until manually unregistered. This helper performs that
//        unregistration on next page load and deletes legacy caches so the broken SW
//        is fully evicted within one user session.
// [INTEGRATION]: Batch 0.5 of the 2026-05-02 release plan. Wired in src/index.js.

// Only delete caches whose names begin with one of these prefixes. Keeps the helper
// safe to run repeatedly without nuking caches owned by unrelated origins / future SW.
const LEGACY_CACHE_PREFIXES = [
  'tabletop-inventory-manager',
  'workbox-',
];

function isLegacyCache(name) {
  if (typeof name !== 'string') return false;
  return LEGACY_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/**
 * Unregister every active service worker and delete every legacy SW cache.
 *
 * @param {object} opts
 * @param {Navigator} opts.navigator
 * @param {CacheStorage} [opts.caches]
 * @returns {Promise<{ran: boolean, reason?: string, unregistered?: number, cachesDeleted?: number, error?: Error}>}
 */
async function unregisterAllServiceWorkers({ navigator, caches }) {
  if (!navigator || !navigator.serviceWorker) {
    return { ran: false, reason: 'no-service-worker-api' };
  }

  let unregistered = 0;
  let cachesDeleted = 0;
  let error;

  // Step 1 — unregister every registration we can find.
  let registrations = [];
  try {
    registrations = await navigator.serviceWorker.getRegistrations();
  } catch (e) {
    error = e;
  }

  if (Array.isArray(registrations) && registrations.length > 0) {
    const settled = await Promise.allSettled(
      registrations.map((reg) => reg.unregister())
    );
    for (const result of settled) {
      if (result.status === 'fulfilled' && result.value) {
        unregistered += 1;
      }
    }
  }

  // Step 2 — clear legacy caches (safe even if SW was never installed).
  if (caches && typeof caches.keys === 'function' && typeof caches.delete === 'function') {
    let keys = [];
    try {
      keys = await caches.keys();
    } catch (_) {
      keys = [];
    }
    const targets = keys.filter(isLegacyCache);
    if (targets.length > 0) {
      const settled = await Promise.allSettled(targets.map((name) => caches.delete(name)));
      for (const result of settled) {
        if (result.status === 'fulfilled' && result.value) {
          cachesDeleted += 1;
        }
      }
    }
  }

  return { ran: true, unregistered, cachesDeleted, error };
}

export { unregisterAllServiceWorkers };
